import { useEffect, useRef, useCallback, useState } from 'react';
import { socketService } from '../services/socket';
import { useMessageStore, useAuthStore } from '../store';
import { notificationSound } from '../services/notificationSound';

// ── Favicon badge ──────────────────────────────────────────────────────────
let _originalFaviconHref = null;

function updateFaviconBadge(count) {
  try {
    let link = document.querySelector("link[rel*='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    if (!_originalFaviconHref) _originalFaviconHref = link.href || '/favicon.ico';

    if (count <= 0) {
      link.href = _originalFaviconHref;
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext('2d');
    const label = count > 99 ? '99+' : String(count);

    const drawBadge = () => {
      ctx.beginPath();
      ctx.arc(24, 8, 9, 0, 2 * Math.PI);
      ctx.fillStyle = '#FF4F6A';
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 9px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, 24, 8);
      link.href = canvas.toDataURL('image/png');
    };

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => { ctx.drawImage(img, 0, 0, 32, 32); drawBadge(); };
    img.onerror = () => {
      ctx.fillStyle = '#003366';
      ctx.fillRect(0, 0, 32, 32);
      drawBadge();
    };
    img.src = _originalFaviconHref;
  } catch (e) {}
}

// ── System notification ───────────────────────────────────────────────────
function showSystemNotification(title, body, conversationId, onClickCb) {
  if (Notification.permission !== 'granted') return;
  const preview = body.length > 60 ? body.substring(0, 57) + '...' : body;
  const n = new Notification(title, {
    body: preview,
    icon: '/icons/icon-192x192.png',
    badge: '/icons/icon-72x72.png',
    tag: 'paychat-message-' + (conversationId || 'general'),
    renotify: true,
    requireInteraction: false,
  });
  n.onclick = () => {
    window.focus();
    n.close();
    if (onClickCb && conversationId) onClickCb(conversationId);
  };
}

// ── Hook ──────────────────────────────────────────────────────────────────
export function useNotifications({ onOpenConversation } = {}) {
  const [inAppNotifs, setInAppNotifs] = useState([]);
  const [soundEnabled, setSoundEnabledState] = useState(notificationSound.isEnabled());
  const [systemNotifEnabled, setSystemNotifEnabled] = useState(
    typeof Notification !== 'undefined' && Notification.permission === 'granted'
  );

  const activeConvId = useMessageStore(s => s.activeConversationId);
  const currentUser = useAuthStore(s => s.user);
  const conversations = useMessageStore(s => s.conversations);

  const activeConvIdRef = useRef(activeConvId);
  const currentUserRef = useRef(currentUser);
  const onOpenRef = useRef(onOpenConversation);

  useEffect(() => { activeConvIdRef.current = activeConvId; }, [activeConvId]);
  useEffect(() => { currentUserRef.current = currentUser; }, [currentUser]);
  useEffect(() => { onOpenRef.current = onOpenConversation; }, [onOpenConversation]);

  // ── Badge + title ────────────────────────────────────────────────────
  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);
  useEffect(() => {
    document.title = totalUnread > 0 ? `(${totalUnread}) PayChat` : 'PayChat';
    updateFaviconBadge(totalUnread);
  }, [totalUnread]);

  useEffect(() => {
    const onFocus = () => {
      const unread = useMessageStore.getState().conversations
        .reduce((s, c) => s + (c.unread_count || 0), 0);
      if (unread === 0) {
        document.title = 'PayChat';
        updateFaviconBadge(0);
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // ── In-app toast helpers ─────────────────────────────────────────────
  const addInAppNotif = useCallback((notif) => {
    setInAppNotifs(prev => {
      const filtered = prev.filter(n => n.id !== notif.id);
      return [...filtered, notif].slice(-3);
    });
  }, []);

  const dismissNotif = useCallback((id) => {
    setInAppNotifs(prev => prev.filter(n => n.id !== id));
  }, []);

  // ── Permission helper ────────────────────────────────────────────────
  const requestPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return 'denied';
    if (Notification.permission === 'default') {
      const perm = await Notification.requestPermission();
      setSystemNotifEnabled(perm === 'granted');
      return perm;
    }
    return Notification.permission;
  }, []);

  // ── Sound preference ─────────────────────────────────────────────────
  const setSoundEnabled = useCallback((v) => {
    notificationSound.setEnabled(v);
    setSoundEnabledState(v);
  }, []);

  // ── Socket event handlers ────────────────────────────────────────────
  const handleNewMessage = useCallback((message) => {
    const myId = currentUserRef.current?.id;
    if (message.sender_id === myId) return;

    const convId = message.conversation_id;
    const isActiveConv = activeConvIdRef.current === convId;
    const isWindowVisible = !document.hidden;

    if (Notification.permission === 'default') {
      Notification.requestPermission().then(p => setSystemNotifEnabled(p === 'granted'));
    }

    const preview = message.is_encrypted
      ? 'Message chiffré 🔒'
      : (message.content || '').substring(0, 60);

    if (!isWindowVisible || !isActiveConv) {
      notificationSound.playMessage();
      showSystemNotification(
        message.sender_name || 'PayChat',
        preview,
        convId,
        (cid) => onOpenRef.current?.(cid)
      );
    }

    if (!isActiveConv) {
      addInAppNotif({
        id: `msg-${message.id || Date.now()}`,
        type: 'message',
        senderName: message.sender_name || 'Inconnu',
        preview,
        conversationId: convId,
      });
    }
  }, [addInAppNotif]);

  const handlePaymentReceived = useCallback((data) => {
    notificationSound.playPayment();
    const amount = data.amount ? `+${data.amount} FCFA` : 'Paiement';
    const preview = data.note ? `${amount} — ${data.note}` : amount;
    addInAppNotif({
      id: `pay-${Date.now()}`,
      type: 'payment',
      senderName: data.sender_name || 'Paiement reçu',
      preview,
      conversationId: data.conversation_id,
    });
    showSystemNotification('Paiement reçu 💸', preview, data.conversation_id,
      (cid) => onOpenRef.current?.(cid));
  }, [addInAppNotif]);

  // Note: call SOUND is handled by the existing WebRTC system (GroupCallPage/callService).
  // We only add an in-app toast here.
  const handleCallIncoming = useCallback((data) => {
    addInAppNotif({
      id: `call-${Date.now()}`,
      type: 'call',
      senderName: data.callerName || data.caller_name || 'Appel entrant',
      preview: data.callType === 'video' ? 'Appel vidéo entrant 📹' : 'Appel audio entrant 📞',
      conversationId: data.conversationId || data.conversation_id,
    });
  }, [addInAppNotif]);

  // ── Subscribe to socket events (survives reconnects via socketService.subscribe) ──
  useEffect(() => {
    socketService.subscribe('new_message', handleNewMessage);
    socketService.subscribe('payment:received', handlePaymentReceived);
    socketService.subscribe('call:incoming', handleCallIncoming);
    return () => {
      socketService.unsubscribe('new_message', handleNewMessage);
      socketService.unsubscribe('payment:received', handlePaymentReceived);
      socketService.unsubscribe('call:incoming', handleCallIncoming);
    };
  }, [handleNewMessage, handlePaymentReceived, handleCallIncoming]);

  return {
    inAppNotifs,
    dismissNotif,
    requestPermission,
    soundEnabled,
    setSoundEnabled,
    systemNotifEnabled,
  };
}
